/* eslint-disable react/no-danger */
/* eslint-disable react/prop-types */

import React, { useContext, useEffect, useState } from "react";
import { graphql, Link, StaticQuery } from "gatsby";
import { AppContext } from "~context/AppContext";
import { DocumentContext } from "~context/DocumentContext";
import Button from "~components/Button";
import Arrow from "~components/svg/Arrow";
import Cross from "~components/svg/Cross";
import CartSquiggle from "~components/svg/CartSquiggle";
import CrossSquashed from "~components/svg/CrossSquashed";
import Logo from "~components/svg/Logo";
import ScreenHeight from "~components/ScreenHeight";
import {
  getPriceByCurrency,
  parseProducts,
  parseSubscriptions
} from "~utils/shopify";
import { useKeyPress } from "~utils/hooks";

const query = graphql`
  query Nav {
    allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "product-page" } } }
    ) {
      edges {
        node {
          fields {
            slug
          }
          frontmatter {
            title
            shopifyHandle
            priority
            deliverable
            withQuantity
          }
        }
      }
    }

    allShopifyProduct {
      edges {
        node {
          id
          title
          description
          handle
          images {
            originalSrc
          }
          productType
          tags
          vendor
          variants {
            id
            title
            image {
              originalSrc
            }
            price
            presentmentPrices {
              edges {
                node {
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
            selectedOptions {
              name
              value
            }
          }
        }
      }
    }

    allShopifyAdminProduct {
      edges {
        node {
          products {
            handle
            variants {
              alternative_id
              inventory_item_id
              title
            }
          }
        }
      }
    }

    allDataYaml {
      edges {
        node {
          cart {
            deliveryNote
          }
          deliveryFee
          deliveryFeeMax
          deliveryMin
        }
      }
    }
  }
`;

const Nav = () => {
  const {
    activeCurrency,
    cart,
    cartActive,
    setCartActive,
    menuActive,
    setMenuActive,
    notificationActive,
    refreshInventoryCache,
    setSubnavLink,
    setSubscriptionOverride,
    toteDelivery,
    setToteDelivery,
    //
    checkout,
    decreaseQuantityByCartIndex,
    increaseQuantityByCartIndex,
    removeFromCartByIndex
  } = useContext(AppContext);
  const { device, scrollTop } = useContext(DocumentContext);

  const [expanded, setExpanded] = useState(false);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [subnavActive, setSubnavActive] = useState(false);

  //

  const close = () => {
    setCartActive(false);
    setMenuActive(false);
  };

  const openMobileCart = () => {
    setCartActive(true);
    setMenuActive(false);
  };

  //

  const escKeyPressed = useKeyPress(`Escape`);

  useEffect(() => {
    close();
  }, [escKeyPressed]);

  // useEffect(() => {
  // if (device === `desktop`) {
  //   close();
  // }
  // }, [scrollTop]);

  //

  return (
    <StaticQuery
      query={query}
      render={data => {
        const products = parseProducts(data);
        const subscriptions = parseSubscriptions(data);

        let deliveryFee;
        let deliveryFeeMax;
        let deliveryMin;
        let deliveryNote;

        if (data?.allDataYaml?.edges?.[0]?.node?.deliveryFee) {
          ({ deliveryFee } = data?.allDataYaml.edges[0].node);
          deliveryFee = parseFloat(deliveryFee);
        }

        if (data?.allDataYaml?.edges?.[0]?.node?.deliveryFeeMax) {
          ({ deliveryFeeMax } = data?.allDataYaml.edges[0].node);
          deliveryFeeMax = parseFloat(deliveryFeeMax);
        }

        if (data?.allDataYaml?.edges?.[0]?.node?.deliveryMin) {
          ({ deliveryMin } = data?.allDataYaml.edges[0].node);
          deliveryMin = parseFloat(deliveryMin);
        }

        if (data?.allDataYaml?.edges?.[0]?.node?.cart) {
          ({ deliveryNote } = data?.allDataYaml.edges[0].node?.cart);
        }

        //

        if (!inventoryLoaded) {
          // setTimeout fixes the static query/render lifecycle warning
          setTimeout(() => {
            setInventoryLoaded(true);
            refreshInventoryCache(products, data.allShopifyAdminProduct);
          });
        }

        //

        let cartCount = 0;
        let cartTotal = 0;
        let freeDelivery = false;
        let hasSubscription = false;
        let hasTote = false;
        let notes = ``;

        //

        const navCart = [];

        cart.forEach(cartItem => {
          if (!cartItem.subscription) {
            return;
          }

          hasSubscription = true;

          subscriptions.forEach(subscription => {
            const variantId = subscription?.variants?.[0]?.id;

            if (variantId && cartItem.variantId === variantId) {
              freeDelivery = true;

              const lineItemCost = parseFloat(
                subscription.variants[0].price
              ).toFixed(2);

              cartCount += cartItem.quantity;
              cartTotal += parseFloat(lineItemCost);

              navCart.push({
                id: variantId,
                image: subscription?.images?.[0]?.originalSrc,
                quantity: 1,
                //
                price: `${parseFloat(lineItemCost).toFixed(2)}`,
                title: subscription.title,
                variantTitle: `Subscription`
              });
            }
          });
        });

        if (!freeDelivery) {
          freeDelivery = true;

          cart.forEach(cartItem => {
            if (!freeDelivery) {
              return;
            }

            products.forEach(productItem => {
              if (!freeDelivery) {
                return;
              }

              productItem.variants.forEach(productItemVariant => {
                if (
                  freeDelivery &&
                  productItemVariant.id === cartItem.variantId &&
                  productItem?.frontmatter?.deliverable
                ) {
                  freeDelivery = false;
                }
              });
            });
          });
        }

        cart.forEach(cartItem => {
          products.forEach(productItem => {
            productItem.variants.forEach(productItemVariant => {
              if (productItemVariant.id === cartItem.variantId) {
                const lineItemCost =
                  getPriceByCurrency(productItemVariant, activeCurrency) *
                  cartItem.quantity;

                cartTotal += lineItemCost;

                const newNavItem = {
                  id: cartItem.variantId,
                  image: cartItem.image || productItemVariant.image.originalSrc,
                  quantity: cartItem.quantity,
                  withQuantity: productItem?.frontmatter?.withQuantity,
                  //
                  price: `${parseFloat(lineItemCost).toFixed(2)}`,
                  slug: productItem.slug,
                  selectedOptions: productItemVariant.selectedOptions,
                  title: productItem.title,
                  variantTitle: productItemVariant.title
                };

                if (productItem.handle === `everlasting-daisy-hemp-tote`) {
                  hasTote = true;
                }

                navCart.push(newNavItem);
              }
            });
          });
        });

        if (!freeDelivery) {
          freeDelivery = parseFloat(cartTotal) > parseFloat(deliveryFeeMax);
        }

        //
        // note generation

        if (hasSubscription) {
          notes = `Subscription // `;
        }

        if (hasTote) {
          if (toteDelivery) {
            notes = `${notes}Tote included; customer opted to use tote for delivery.`;
          } else {
            notes = `${notes}Tote included; delivered separately.`;
          }
        }

        //

        return (
          <ScreenHeight
            className={`nav ${cartActive ? `cart-active` : ``} ${
              menuActive ? `menu-active` : ``
            } ${
              notificationActive ? `notification-active` : ``
            } w-screen fixed flex items-center justify-between z-40 pointer-events-none`}
          >
            <div
              role="presentation"
              className={`nav__background ${
                cartActive ? `opacity-50 pointer-events-auto` : `opacity-0`
              } transition-opacity w-screen h-screen fixed top-0 right-0 bottom-0 left-0 z-10 cursor-pointer bg-black`}
              onClick={close}
            ></div>

            <div
              className={`nav__menu ${
                menuActive ? `pointer-events-auto` : ``
              } h-full absolute top-0 left-0 pt-24 z-20 overflow-y-scroll bg-cream text-dark-green`}
            >
              <div className="w-full absolute top-0 right-0 left-0 pt-5 sm:pt-8">
                <div className="grid">
                  <div className="grid-end-5 relative z-20">
                    <Link
                      to="/"
                      className="animation-appear-down animation-delay-2 block pointer-events-auto"
                      onClick={close}
                    >
                      <Logo
                        className="header__link sm:h-12"
                        color="dark-green"
                      />
                    </Link>
                  </div>

                  <div className="touch-only-flex grid-end-7 relative flex items-center justify-end">
                    <button
                      type="button"
                      className="relative block pointer-events-auto"
                      onClick={close}
                    >
                      <Cross className="w-8 h-8" color="dark-green" />
                    </button>
                  </div>
                </div>
              </div>

              {menuActive && (
                <>
                  <ul className="animation-appear-down-slow animation-delay-3">
                    <li className="px-8 f3 text-center">
                      <button
                        type="button"
                        className="w-full relative block"
                        onClick={() => setSubnavActive(!subnavActive)}
                      >
                        <div className="w-full relative flex items-center justify-center text-center">
                          <span>Store</span>
                          <Arrow
                            className={`header__links__arrow ${
                              subnavActive ? `inverted` : ``
                            } w-4 block ml-3`}
                            color="dark-green"
                          />
                        </div>
                      </button>
                    </li>

                    {subnavActive && (
                      <li className="animation-appear-down-slow animation-delay-1 w-full relative block px-8 text-orange">
                        <Link
                          to="/products"
                          className="block break-words f3 text-center text-orange"
                          onClick={() => {
                            setSubnavLink(`preorder`);
                            close();
                          }}
                        >
                          Saturday Pre-Orders
                        </Link>
                      </li>
                    )}

                    {subnavActive && (
                      <li className="animation-appear-down-slow animation-delay-2 w-full relative block mb-4 px-8 text-orange">
                        <Link
                          to="/products"
                          className="block break-words f3 text-center text-orange"
                          onClick={() => {
                            setSubnavLink(`subscription`);
                            setSubscriptionOverride(true);
                            close();
                          }}
                        >
                          Sourdough Subscription
                        </Link>
                      </li>
                    )}

                    <li className="px-8 f3 text-center">
                      <Link to="/about" className="block" onClick={close}>
                        About
                      </Link>
                    </li>

                    <li className="mt-8 px-8 text-center">
                      <button
                        type="button"
                        className="w-full relative block text-center"
                        onClick={openMobileCart}
                      >
                        <div className="flex items-start justify-center">
                          <span className="text-dark-green f3">Cart</span>
                          <span className="ml-2 mt-1 text-light-green f5">
                            {cartCount}
                          </span>
                        </div>
                      </button>
                    </li>

                    <li className="px-8 f3 text-center">
                      <a href="https://orders.wildwoodbakery.com.au/account">
                        Account
                      </a>
                    </li>
                  </ul>

                  <figure
                    className={`${
                      device === `tablet` ? `w-1/2 mx-auto` : `w-full`
                    } relative mt-8 mb-8 px-8`}
                  >
                    <img
                      className="w-full relative block"
                      src="/uploads/squiggle-sm-green.svg"
                      alt="Green squiggle"
                    />
                  </figure>
                </>
              )}

              {menuActive && (
                <div className="animation-appear-down-slow animation-delay-4">
                  <h4 className="mt-2 b1 text-center text-light-green">
                    Resources
                  </h4>

                  <ul>
                    <li className="animation-appear-down-slow animation-delay-3 f3 text-light-green text-center">
                      <Link to="/faqs" className="block" onClick={close}>
                        FAQs
                      </Link>
                    </li>

                    <li className="animation-appear-down-slow animation-delay-3 f3 text-light-green text-center">
                      <Link
                        to="/faqs/#faq-deliveries"
                        className="block"
                        onClick={close}
                      >
                        Deliveries
                      </Link>
                    </li>

                    <li className="animation-appear-down-slow animation-delay-3 f3 text-light-green text-center">
                      <Link
                        to="/faqs#faq-wholesale"
                        className="block"
                        onClick={close}
                      >
                        Wholesale
                      </Link>
                    </li>

                    <li className="animation-appear-down-slow animation-delay-3 f3 text-light-green text-center">
                      <Link
                        to="/faqs#faq-sustainability"
                        className="block"
                        onClick={close}
                      >
                        Sustainability
                      </Link>
                    </li>
                  </ul>

                  {/* // */}

                  <h4 className="mt-6 b1 text-center text-light-green">
                    Connect
                  </h4>

                  <ul>
                    <li className="animation-appear-down-slow animation-delay-3 f3 text-light-green text-center">
                      <a
                        href="https://www.instagram.com/wildwoodbakerypreston/"
                        rel="noopener noreferrer"
                        target="_blank"
                        className="block"
                      >
                        Instagram
                      </a>
                    </li>

                    <li className="animation-appear-down-slow animation-delay-3 f3 text-light-green text-center">
                      <a
                        href="https://www.facebook.com/wildwoodbakerypreston/"
                        rel="noopener noreferrer"
                        target="_blank"
                        className="block"
                      >
                        Facebook
                      </a>
                    </li>
                  </ul>

                  {/* // */}

                  <h4 className="mt-6 b1 text-center text-light-green">
                    Contact
                  </h4>

                  <h3 className="animation-appear-down-slow animation-delay-3 break-words mb-6 px-8 f3 text-light-green text-center">
                    <a
                      href="mailto:info@wildwoodbakery.com.au"
                      className="block"
                    >
                      info@wildwoodbakery.com.au
                    </a>
                  </h3>

                  {/* // */}

                  <div className="w-full relative block mt-24">
                    <img
                      className="w-full relative block"
                      src="/uploads/footer-wave.png"
                      alt="Grassland"
                    />

                    <div className="w-full absolute bottom-0 right-0 left-0 z-10 flex items-center justify-center">
                      <button
                        type="button"
                        className="relative block pt-4 pb-6"
                        onClick={() => setExpanded(!expanded)}
                      >
                        <span
                          className={`${
                            device && device === `desktop` ? `b1` : `f5`
                          } text-cream`}
                        >
                          Site credit
                        </span>
                        <span
                          className={`ml-2 ${
                            device && device === `desktop` ? `b1` : `f5`
                          } text-cream`}
                        >
                          {expanded ? `-` : `+`}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {expanded && (
                <div className="w-full relative flex items-center justify-center pt-5 px-6 pb-5 bg-dark-green">
                  <h4 className="w-full relative block break-words f5 text-center">
                    <span className="text-light-green">
                      Creative direction and design:
                    </span>
                    <span className="ml-2 text-orange">Anna Topalidou.</span>

                    <span className="ml-2 text-light-green">Photography:</span>
                    <span className="ml-2 text-orange">
                      Phoebe Veldhuizen, Anna Topalidou.
                    </span>

                    <span className="ml-2 text-light-green">
                      Site development:
                    </span>
                    <span className="ml-2 text-orange">Dan Courtness.</span>

                    <span className="ml-2 text-light-green">Illustration:</span>
                    <span className="ml-2 text-orange">
                      Mari Adams - Calamari Pictures.
                    </span>
                  </h4>
                </div>
              )}
            </div>

            <div
              className={`nav__cart  ${
                cartActive ? `pointer-events-auto` : ``
              } h-full absolute top-0 right-0 z-20 flex flex-col items-stretch pt-3 md:pt-8 bg-dark-green text-cream`}
            >
              <header className="w-full relative flex justify-between px-5 md:px-10">
                <h2 className="mt-2 md:mt-0 f4 text-orange">Your Cart</h2>

                <button
                  className="w-12 h-12 relative md:mt-0 -mr-2 md:mr-0 block"
                  onClick={close}
                  type="button"
                >
                  <div className="w-full h-full relative flex items-center justify-center">
                    <CrossSquashed className="w-8 h-8" color="orange" />
                  </div>
                </button>
              </header>

              {cartActive && (
                <>
                  {(navCart?.[0] && (
                    <ul
                      className="nav__cart__list relative self-stretch px-5 md:px-10 overflow-y-scroll"
                      style={{ flex: `1 1` }}
                    >
                      {navCart.map((cartItem, cartItemIndex) => {
                        return (
                          <li
                            key={cartItem.id}
                            className="nav__cart__item w-full relative mt-8"
                          >
                            <div className="relative flex mb-4">
                              <div className="w-1/4 relative block">
                                <figure className="square">
                                  <img
                                    className="w-full absolute transform-center"
                                    src={cartItem.image}
                                    alt={cartItem.title}
                                  />
                                </figure>
                              </div>

                              <div className="w-3/4 relative flex flex-col justify-between px-4">
                                <h2
                                  className={`${
                                    device === `desktop` ? `b1` : `b2`
                                  }`}
                                >
                                  {cartItem.title}
                                </h2>

                                <div className="w-full relative flex items-center justify-between md:mb-4">
                                  {cartItem?.withQuantity && (
                                    <div className="h-full relative flex items-center">
                                      <button
                                        type="button"
                                        className="h-full block pr-4 b1 text-orange"
                                        onClick={() =>
                                          decreaseQuantityByCartIndex(
                                            cartItemIndex
                                          )
                                        }
                                      >
                                        -
                                      </button>

                                      <h4
                                        className={`${
                                          device === `desktop` ? `b1` : `b2`
                                        }`}
                                      >
                                        {cartItem.quantity}
                                      </h4>

                                      <button
                                        type="button"
                                        className="h-full block pl-4 b1 text-orange"
                                        onClick={() =>
                                          increaseQuantityByCartIndex(
                                            cartItemIndex
                                          )
                                        }
                                      >
                                        +
                                      </button>
                                    </div>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeFromCartByIndex(cartItemIndex)
                                    }
                                    className={`hover-underline ${
                                      device === `desktop` ? `b1` : `b2`
                                    } text-orange`}
                                  >
                                    Remove
                                  </button>

                                  <h3 className="b1">{cartItem.price}</h3>
                                </div>
                              </div>
                            </div>

                            {(device === `tablet` && (
                              <img
                                className="w-full relative block"
                                src="/uploads/squiggle-md.svg"
                                alt="Squiggle line"
                              />
                            )) || (
                              <CartSquiggle className="w-full relative block" />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )) || (
                    <div className="w-full h-full relative flex flex-col items-center justify-center pb-24">
                      <img
                        className="animation-appear-slow animation-delay-2 w-1/2 md:w-3/5 relative block mb-4 md:mb-10 mx-auto"
                        src="/uploads/hand.png"
                        alt="Hand holding mushroom"
                      />

                      <h3
                        className={`animation-appear-slow animation-delay-3 ${
                          device === `desktop` ? `f5` : `f4`
                        } text-center text-orange`}
                      >
                        Your cart is empty.
                      </h3>
                      <h3
                        className={`animation-appear-slow animation-delay-4 ${
                          device === `desktop` ? `f5` : `f4`
                        } text-center text-cream`}
                      >
                        Find something special!
                      </h3>
                    </div>
                  )}
                </>
              )}

              <div className="w-full relative z-10">
                {navCart?.[0] && (
                  <div className="relative mb-6 pt-3 md:pt-0 px-5 md:px-10">
                    {(!hasTote && (
                      <p
                        className={`animation-appear-slow mb-4 md:mt-6 ${
                          device === `desktop` ? `b2` : `caption`
                        }`}
                        style={{ lineHeight: 1.5 }}
                        dangerouslySetInnerHTML={{ __html: deliveryNote || `` }}
                      ></p>
                    )) || (
                      <p
                        className={`animation-appear-slow mb-4 md:mt-6 ${
                          device === `desktop` ? `b2` : `caption`
                        }`}
                        style={{ lineHeight: 1.5 }}
                      >
                        <span>
                          {toteDelivery
                            ? `We are using your new tote for delivery.`
                            : `We will deliver your new tote separately.`}
                        </span>
                        <span>{` `}(</span>
                        <button
                          type="button"
                          className="hover-underline inline-block text-orange"
                          onClick={() => setToteDelivery(!toteDelivery)}
                        >
                          Change
                        </button>
                        <span>)</span>
                      </p>
                    )}

                    {device && device === `desktop` && (
                      <CartSquiggle className="w-full relative block mb-6 md:mb-12" />
                    )}

                    {(!freeDelivery && (
                      <div className="relative flex ites-center justify-between mt-4 md:mt-12">
                        <h4 className="b1 text-orange">Delivery</h4>
                        <h4 className="b1 text-orange">
                          {cartTotal >= deliveryMin ? deliveryFee : `-`}
                        </h4>
                      </div>
                    )) || (
                      <h4 className="b1 text-orange">
                        Hey presto! You qualify for free delivery.
                      </h4>
                    )}

                    <div
                      className={`relative flex ites-center justify-between mt-2 ${
                        cartTotal < deliveryMin ? `` : `md:pb-6`
                      }`}
                    >
                      <h3 className={`${device === `desktop` ? `f3` : `f4`}`}>
                        Subtotal
                      </h3>
                      <h3 className={`${device === `desktop` ? `f3` : `f4`}`}>
                        {parseFloat(cartTotal).toFixed(2)}
                      </h3>
                    </div>

                    {cartTotal < deliveryMin && (
                      <h4
                        className={`mt-2 md:mt-4 whitespace-no-wrap ${
                          device === `desktop` ? `f3` : `f4`
                        } text-center text-orange`}
                      >
                        — Minimum order ${deliveryMin} —
                      </h4>
                    )}
                  </div>
                )}

                {(navCart?.[0] && cartTotal >= deliveryMin && (
                  <Button
                    className="w-full pt-5 pb-5"
                    color="orange"
                    onClick={() => checkout(notes)}
                  >
                    <span className="f-button">Complete order</span>
                  </Button>
                )) || (
                  <Link to="/products" onClick={close}>
                    <Button className="w-full pt-5 pb-5" color="cream">
                      <span className="f-button">Return to store</span>
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </ScreenHeight>
        );
      }}
    />
  );
};

export default Nav;
